import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminGuard from '../../components/admin/AdminGuard';
import AdminImageInput from '../../components/admin/AdminImageInput';
import AdminFileInput from '../../components/admin/AdminFileInput';
import { fetchProjectWithSectionsAdmin, saveProjectAdmin } from '../../services/adminService';

const TYPE_OPTIONS = [
  { value: 'uiux',         label: 'UI/UX' },
  { value: 'illustration', label: 'Illustration' },
  { value: 'content',      label: 'Content' },
  { value: 'log',          label: 'Writing (Log)' },
  { value: 'download',     label: 'Download (Asset)' },
];

function titleToType(title) {
  return title.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || 'content';
}

function getYouTubeId(url = '') {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m?.[1] || null;
}

// 시스템 예약 섹션 타입
const SYSTEM_SECTION_TYPES = ['cover', 'short_description', 'image', 'screen'];

// ── DB 섹션 → 폼 상태 ──────────────────────────────────────────────────────────
function buildForm(sections = []) {
  const sorted = [...sections].sort((a, b) => a.order_index - b.order_index);
  const cover  = sorted.find((s) => s.section_type === 'cover');

  const contentSections = sorted
    .filter((s) => !SYSTEM_SECTION_TYPES.includes(s.section_type))
    .map((s) => {
      const imgs = [
        ...(s.image_url ? [{ url: s.image_url, alt: s.title || '' }] : []),
        ...((s.extra_images || []).map((img) =>
          typeof img === 'string' ? { url: img, alt: '' } : { url: img.url || '', alt: img.alt || '' }
        )),
      ].filter((img) => img.url);
      return {
        title:      s.title || '',
        content:    s.content || '',
        images:     imgs,
        videoUrl:   s.video_url || '',
        is_visible: s.is_visible !== false,
        textSize:   s.text_size || 'md',
        textWeight: s.text_weight || 'regular',
      };
    });

  return {
    heroBgColor:     cover?.background_color || '#0a0a0a',
    contentSections: contentSections.length ? contentSections : [EMPTY_SECTION()],
  };
}

// ── 폼 상태 → DB rows ──────────────────────────────────────────────────────────
function buildSections(form, shortDescription = '') {
  const rows = [];
  let i = 0;

  // 히어로 배경색 (cover 섹션)
  rows.push({
    section_type:     'cover',
    background_color: form.heroBgColor || '#0a0a0a',
    is_visible:       true,
    downloadable:     false,
    extra_images:     [],
    order_index:      i++,
  });

  // 한 줄 설명
  if (shortDescription.trim()) {
    rows.push({
      section_type: 'short_description',
      title:        'Short Description',
      content:      shortDescription.trim(),
      is_visible:   true,
      downloadable: false,
      extra_images: [],
      order_index:  i++,
    });
  }

  // 콘텐츠 섹션
  for (const sec of form.contentSections) {
    const validImgs = sec.images.filter((img) => img.url.trim());
    if (!sec.title.trim() && !sec.content.trim() && validImgs.length === 0 && !sec.videoUrl?.trim()) continue;
    rows.push({
      section_type:  titleToType(sec.title) || 'content',
      title:         sec.title,
      content:       sec.content,
      image_url:     validImgs[0]?.url || null,
      extra_images:  validImgs.slice(1).map((img) => ({ url: img.url, alt: img.alt })),
      video_url:     sec.videoUrl?.trim() || null,
      is_visible:    sec.is_visible,
      downloadable:  false,
      text_size:     sec.textSize || 'md',
      text_weight:   sec.textWeight || 'regular',
      order_index:   i++,
    });
  }
  return rows;
}

const EMPTY_SECTION = () => ({ title: '', content: '', images: [], videoUrl: '', is_visible: true, textSize: 'md', textWeight: 'regular' });

const TEXT_SIZE_OPTIONS   = [{ value: 'sm', label: '작게' }, { value: 'md', label: '보통' }, { value: 'lg', label: '크게' }];
const TEXT_WEIGHT_OPTIONS = [{ value: 'regular', label: '보통' }, { value: 'bold', label: '굵게' }];

const isDownload = (type) => type === 'download';
const isWriting  = (type) => type === 'log';
// 팝업(IllustrationModal)으로 뜨는 타입 — Work 상세 페이지 대신 갤러리 모달을 사용
const isGallery  = (type) => type === 'illustration' || type === 'content';

const EMPTY_GALLERY_IMAGE = () => ({ url: '', alt: '' });

// ── 컴포넌트 ───────────────────────────────────────────────────────────────────
export default function AdminProjectFormPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isNew    = !id;

  const [proj, setProj] = useState({
    title: '', project_type: 'uiux', role: '', project_date: '',
    thumbnail_url: '', mobile_thumbnail_url: '',
    tags: '', shortDescription: '', external_url: '', link_type: 'download',
    is_visible: true, is_main_only: false,
    work_period: '', work_type: '개인작업', contribution: '', tools: '', link_url: '',
    // 팝업(갤러리) 전용 필드
    summary: '', description: '', galleryImages: [EMPTY_GALLERY_IMAGE()],
  });
  const [form, setForm] = useState({
    heroBgColor:     '#0a0a0a',
    contentSections: [EMPTY_SECTION()],
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const sectionTextareaRefs = useRef({});

  useEffect(() => {
    if (!isNew) {
      fetchProjectWithSectionsAdmin(id).then((data) => {
        if (!data) return;
        const sections  = data.project_sections || [];
        const sdSection = sections.find((s) => s.section_type === 'short_description');
        const summarySection = sections.find((s) => s.section_type === 'summary');
        const descSection    = sections.find((s) => s.section_type === 'description');
        // 예전 방식(자유 이름 섹션)으로 저장된 팝업 프로젝트를 위한 1회성 마이그레이션:
        // summary/description 섹션이 없으면, 이름이 뭐든 남아있는 일반 섹션 본문/이미지를
        // 상세 설명·갤러리로 끌어와 보여준다 (그대로 저장하면 깔끔한 섹션으로 정리됨)
        const KNOWN_TYPES = ['cover', 'short_description', 'image', 'screen', 'summary', 'description'];
        const legacySections = sections
          .filter((s) => !KNOWN_TYPES.includes(s.section_type) && s.is_visible !== false)
          .sort((a, b) => a.order_index - b.order_index);
        const legacyExtra = legacySections.map((s) => s.content).filter(Boolean).join('\n\n');
        const legacyImages = legacySections.flatMap((s) => [
          ...(s.image_url ? [{ url: s.image_url, alt: s.title || '' }] : []),
          ...((s.extra_images || []).map((img) =>
            typeof img === 'string' ? { url: img, alt: '' } : { url: img.url || '', alt: img.alt || '' }
          )),
        ]).filter((img) => img.url);

        const galleryImages = [...sections]
          .filter((s) => s.section_type === 'image' && s.is_visible !== false)
          .sort((a, b) => a.order_index - b.order_index)
          .map((s) => ({ url: s.image_url || '', alt: s.title || '' }))
          .concat(legacyImages);
        setProj({
          title:                data.title || '',
          project_type:         data.project_type || 'uiux',
          role:                 data.role || '',
          project_date:         data.project_date || '',
          thumbnail_url:        data.thumbnail_url || '',
          mobile_thumbnail_url: data.mobile_thumbnail_url || '',
          tags:                 (data.tags || []).join(', '),
          shortDescription:     sdSection?.content || '',
          external_url:         data.external_url || '',
          link_type:            data.link_type || 'download',
          is_visible:           data.is_visible !== false,
          is_main_only:         data.is_main_only || false,
          work_period:          data.work_period || '',
          work_type:            data.work_type || '개인작업',
          contribution:         data.contribution || '',
          tools:                data.tools || '',
          link_url:             data.link_url || '',
          summary:              summarySection?.content || '',
          description:          descSection?.content || legacyExtra || '',
          galleryImages:        galleryImages.length ? galleryImages : [EMPTY_GALLERY_IMAGE()],
        });
        setForm(buildForm(sections));
      });
    }
  }, [id, isNew]);

  const sp     = (key) => (e) => setProj((p) => ({ ...p, [key]: e.target.value }));
  const spBool = (key, val) => setProj((p) => ({ ...p, [key]: val }));

  // 섹션 조작
  const updateSection   = (idx, field, val) =>
    setForm((f) => ({ ...f, contentSections: f.contentSections.map((s, i) => i === idx ? { ...s, [field]: val } : s) }));
  const addSection      = () =>
    setForm((f) => ({ ...f, contentSections: [...f.contentSections, EMPTY_SECTION()] }));
  const removeSection   = (idx) =>
    setForm((f) => ({ ...f, contentSections: f.contentSections.filter((_, i) => i !== idx) }));
  const moveSection     = (idx, dir) =>
    setForm((f) => {
      const next = [...f.contentSections]; const t = idx + dir;
      if (t < 0 || t >= next.length) return f;
      [next[idx], next[t]] = [next[t], next[idx]];
      return { ...f, contentSections: next };
    });

  // 선택한 텍스트만 굵게/크게 — 텍스트에어리어의 선택 영역을 마커로 감싼다
  // (렌더링 쪽에서 **글자**는 굵게, ++글자++는 크게로 해석함)
  const applyInlineMarkup = (idx, marker) => {
    const el = sectionTextareaRefs.current[idx];
    if (!el) return;
    const { selectionStart: start, selectionEnd: end, value } = el;
    if (start === end) return; // 선택된 텍스트 없으면 무시
    const wrapped = `${marker}${value.slice(start, end)}${marker}`;
    const nextValue = value.slice(0, start) + wrapped + value.slice(end);
    updateSection(idx, 'content', nextValue);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start, start + wrapped.length);
    });
  };

  // 섹션 내 이미지
  const addSectionImage    = (si) =>
    setForm((f) => ({ ...f, contentSections: f.contentSections.map((s, i) => i === si ? { ...s, images: [...s.images, { url: '', alt: '' }] } : s) }));
  const updateSectionImage = (si, ji, field, val) =>
    setForm((f) => ({ ...f, contentSections: f.contentSections.map((s, i) => i === si ? { ...s, images: s.images.map((img, j) => j === ji ? { ...img, [field]: val } : img) } : s) }));
  const removeSectionImage = (si, ji) =>
    setForm((f) => ({ ...f, contentSections: f.contentSections.map((s, i) => i === si ? { ...s, images: s.images.filter((_, j) => j !== ji) } : s) }));

  // 갤러리(팝업) 이미지 — Illustration/Content 타입 전용
  const addGalleryImage    = () =>
    setProj((p) => ({ ...p, galleryImages: [...p.galleryImages, EMPTY_GALLERY_IMAGE()] }));
  const updateGalleryImage = (idx, field, val) =>
    setProj((p) => ({ ...p, galleryImages: p.galleryImages.map((img, i) => i === idx ? { ...img, [field]: val } : img) }));
  const removeGalleryImage = (idx) =>
    setProj((p) => ({ ...p, galleryImages: p.galleryImages.filter((_, i) => i !== idx) }));
  const moveGalleryImage   = (idx, dir) =>
    setProj((p) => {
      const next = [...p.galleryImages]; const t = idx + dir;
      if (t < 0 || t >= next.length) return p;
      [next[idx], next[t]] = [next[t], next[idx]];
      return { ...p, galleryImages: next };
    });

  // 이 프로젝트에 이미 삽입된 이미지들 — 썸네일 크롭 소스로 선택 가능하게 모아둠 (중복 제거)
  const pickableThumbnailImages = useMemo(() => {
    const seen = new Set();
    const pool = [];
    const consider = (img) => {
      const url = img.url?.trim();
      if (!url || seen.has(url)) return;
      seen.add(url);
      pool.push({ url, alt: img.alt || '' });
    };
    proj.galleryImages.forEach(consider);
    form.contentSections.forEach((s) => s.images.forEach(consider));
    return pool;
  }, [proj.galleryImages, form.contentSections]);

  // 저장
  const handleSave = async () => {
    if (!proj.title.trim()) { setError('제목을 입력해주세요.'); return; }
    setSaving(true); setError('');
    try {
      const projectData = {
        ...(isNew ? {} : { id }),
        title:                proj.title,
        project_type:         proj.project_type,
        role:                 proj.role,
        project_date:         proj.project_date || null,
        thumbnail_url:        proj.thumbnail_url,
        mobile_thumbnail_url: proj.mobile_thumbnail_url || null,
        tags:                 proj.tags.split(',').map((t) => t.trim()).filter(Boolean),
        external_url:         proj.external_url || null,
        link_type:            proj.link_type || 'download',
        is_visible:           proj.is_visible,
        is_main_only:         proj.is_main_only,
        work_period:          proj.work_period || null,
        work_type:            proj.work_type || null,
        contribution:         proj.contribution || null,
        tools:                proj.tools || null,
        link_url:             proj.link_url || null,
      };

      let sections;
      if (proj.is_main_only || isDownload(proj.project_type)) {
        // 다운로드/메인전용: cover 섹션 + short_description만
        sections = [
          { section_type: 'cover', background_color: form.heroBgColor, is_visible: true, downloadable: false, extra_images: [], order_index: 0 },
          ...(proj.shortDescription.trim()
            ? [{ section_type: 'short_description', title: 'Short Description', content: proj.shortDescription.trim(), is_visible: true, downloadable: false, extra_images: [], order_index: 1 }]
            : []),
        ];
      } else if (isGallery(proj.project_type)) {
        // Illustration/Content(팝업): cover + short_description + summary + description + 갤러리 이미지
        let gi = 0;
        sections = [
          { section_type: 'cover', background_color: form.heroBgColor, is_visible: true, downloadable: false, extra_images: [], order_index: gi++ },
          ...(proj.shortDescription.trim()
            ? [{ section_type: 'short_description', title: 'Short Description', content: proj.shortDescription.trim(), is_visible: true, downloadable: false, extra_images: [], order_index: gi++ }]
            : []),
          ...(proj.summary.trim()
            ? [{ section_type: 'summary', title: 'Summary', content: proj.summary.trim(), is_visible: true, downloadable: false, extra_images: [], order_index: gi++ }]
            : []),
          ...(proj.description.trim()
            ? [{ section_type: 'description', title: 'Description', content: proj.description.trim(), is_visible: true, downloadable: false, extra_images: [], order_index: gi++ }]
            : []),
          ...proj.galleryImages
            .filter((img) => img.url.trim())
            .map((img) => ({
              section_type: 'image', title: img.alt || '', content: '',
              image_url: img.url, extra_images: [], is_visible: true, downloadable: false,
              order_index: gi++,
            })),
        ];
      } else {
        sections = buildSections(form, proj.shortDescription);
      }

      await saveProjectAdmin(projectData, sections);
      navigate('/admin/dashboard');
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const roleLabel = isWriting(proj.project_type) ? '카테고리' : '역할';
  const rolePlaceholder = isWriting(proj.project_type) ? 'UX Design, Systems…' : 'Lead Designer';

  return (
    <AdminGuard>
      <div className="admin-page">
        <header className="admin-header">
          <button onClick={() => navigate('/admin/dashboard')} className="admin-btn-ghost">← 목록</button>
          <span className="admin-header-title">{isNew ? '새 항목' : '편집'}</span>
          <button onClick={handleSave} disabled={saving} className="admin-btn-primary">
            {saving ? '저장 중…' : '저장'}
          </button>
        </header>

        <main className="admin-main">
          {error && <p className="admin-error">{error}</p>}

          {/* ── 기본 정보 ─────────────────────────────────────────────── */}
          <section className="admin-form-section">
            <h3 className="admin-form-section-title">기본 정보</h3>
            <div className="admin-form-grid">

              <label className="admin-label" style={{ gridColumn: '1 / -1' }}>제목 * (Enter로 줄바꿈 가능)
                <textarea className="admin-textarea" rows={2} value={proj.title} onChange={sp('title')} placeholder="제목" />
              </label>

              {/* 게시물 유형 */}
              <div className="admin-label" style={{ gridColumn: '1 / -1' }}>
                <span className="admin-field-label">게시물 유형</span>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button type="button"
                    className={`admin-btn-toggle${!proj.is_main_only ? ' admin-btn-toggle--on' : ''}`}
                    onClick={() => spBool('is_main_only', false)}>
                    일반 (상세 페이지 있음)
                  </button>
                  <button type="button"
                    className={`admin-btn-toggle${proj.is_main_only ? ' admin-btn-toggle--on' : ''}`}
                    onClick={() => spBool('is_main_only', true)}>
                    메인 전용 (상세 없음)
                  </button>
                </div>
              </div>

              {/* 노출 */}
              <div className="admin-label" style={{ gridColumn: '1 / -1' }}>
                <span className="admin-field-label">노출 상태</span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button"
                    className={`admin-btn-toggle${proj.is_visible ? ' admin-btn-toggle--on' : ''}`}
                    onClick={() => spBool('is_visible', true)}>노출</button>
                  <button type="button"
                    className={`admin-btn-toggle${!proj.is_visible ? ' admin-btn-toggle--on admin-btn-toggle--danger' : ''}`}
                    onClick={() => spBool('is_visible', false)}>숨김</button>
                </div>
              </div>

              {/* 타입 */}
              <label className="admin-label">타입
                <select className="admin-select" value={proj.project_type} onChange={sp('project_type')}>
                  {TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </label>

              {/* 연도 */}
              <label className="admin-label">연도
                <input className="admin-input" type="date" value={proj.project_date} onChange={sp('project_date')} />
              </label>

              {/* 역할 / 카테고리 */}
              <label className="admin-label">{roleLabel}
                <input className="admin-input" value={proj.role} onChange={sp('role')} placeholder={rolePlaceholder} />
              </label>

              {/* 태그 */}
              <label className="admin-label">태그 (쉼표 구분)
                <input className="admin-input" value={proj.tags} onChange={sp('tags')} placeholder="UX Design, Mobile" />
              </label>

              {/* 상세 페이지 정보 박스 — 다운로드 제외 */}
              {!isDownload(proj.project_type) && (
                <>
                  <label className="admin-label">작업 기간
                    <input className="admin-input" value={proj.work_period} onChange={sp('work_period')} placeholder="2024.01 – 2024.03" />
                  </label>

                  <label className="admin-label">기여도
                    <input className="admin-input" value={proj.contribution} onChange={sp('contribution')} placeholder="100% 또는 UI 디자인 70%" />
                  </label>

                  <div className="admin-label" style={{ gridColumn: '1 / -1' }}>
                    <span className="admin-field-label">작업 성격</span>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {['개인작업', '사이드프로젝트', '프로젝트'].map((v) => (
                        <button key={v} type="button"
                          className={`admin-btn-toggle${proj.work_type === v ? ' admin-btn-toggle--on' : ''}`}
                          onClick={() => spBool('work_type', v)}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="admin-label" style={{ gridColumn: '1 / -1' }}>작업 툴 (쉼표 구분)
                    <input className="admin-input" value={proj.tools} onChange={sp('tools')} placeholder="Figma, Blender, ChatGPT" />
                  </label>

                  <label className="admin-label" style={{ gridColumn: '1 / -1' }}>링크
                    <input className="admin-input" value={proj.link_url} onChange={sp('link_url')} placeholder="https://..." />
                  </label>
                </>
              )}

              {/* 다운로드 전용: 버튼 타입 + 파일/링크 */}
              {isDownload(proj.project_type) && (
                <>
                  <div className="admin-label" style={{ gridColumn: '1 / -1' }}>
                    <span className="admin-field-label">버튼 동작</span>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.375rem' }}>
                      <button type="button"
                        className={`admin-btn-toggle${proj.link_type === 'download' ? ' admin-btn-toggle--on' : ''}`}
                        onClick={() => setProj((p) => ({ ...p, link_type: 'download' }))}>
                        ↓ 파일 다운로드
                      </button>
                      <button type="button"
                        className={`admin-btn-toggle${proj.link_type === 'link' ? ' admin-btn-toggle--on' : ''}`}
                        onClick={() => setProj((p) => ({ ...p, link_type: 'link' }))}>
                        ↗ 외부 링크 열기
                      </button>
                    </div>
                    <p className="admin-field-hint" style={{ marginTop: '0.375rem' }}>
                      {proj.link_type === 'download'
                        ? '클릭 시 파일이 바로 다운로드됩니다.'
                        : '클릭 시 새 탭에서 링크가 열립니다.'}
                    </p>
                  </div>

                  <div className="admin-label" style={{ gridColumn: '1 / -1' }}>
                    <span className="admin-field-label">
                      {proj.link_type === 'download' ? '다운로드 파일' : '외부 링크'}
                      <span className="admin-field-badge" style={{ marginLeft: '0.5rem' }}>Asset 카드 버튼에서 사용</span>
                    </span>
                    <AdminFileInput
                      value={proj.external_url}
                      onChange={(url) => setProj((p) => ({ ...p, external_url: url }))}
                      label={proj.link_type === 'download' ? '파일 업로드' : '링크 입력'}
                    />
                  </div>
                </>
              )}

            </div>
          </section>

          {/* ── 히어로 영역 ───────────────────────────────────────────── */}
          <section className="admin-form-section">
            <h3 className="admin-form-section-title">히어로 영역</h3>
            <p className="admin-field-hint" style={{ marginBottom: '1rem' }}>
              상세 페이지 첫 화면 배경입니다. 이미지가 없거나 투명할 때 배경색이 표시됩니다.
            </p>
            <div className="admin-form-grid">

              {/* 히어로 이미지 */}
              <label className="admin-label" style={{ gridColumn: '1 / -1' }}>
                히어로 이미지
                <span className="admin-field-badge">상세 배경 · 메인 캐러셀 · 리스트 공용</span>
                <AdminImageInput
                  value={proj.thumbnail_url}
                  onChange={(url) => setProj((p) => ({ ...p, thumbnail_url: url }))}
                  pickableImages={pickableThumbnailImages}
                  cropAspect={7 / 4}
                />
              </label>

              {/* 모바일 이미지 */}
              {!isDownload(proj.project_type) && (
                <label className="admin-label" style={{ gridColumn: '1 / -1' }}>
                  모바일 이미지
                  <span className="admin-field-badge">모바일 캐러셀 전용 (없으면 히어로 이미지 사용)</span>
                  <AdminImageInput
                    value={proj.mobile_thumbnail_url}
                    onChange={(url) => setProj((p) => ({ ...p, mobile_thumbnail_url: url }))}
                    placeholder="모바일 전용 이미지 URL 또는 업로드"
                  />
                </label>
              )}

              {/* 히어로 배경색 */}
              <label className="admin-label">
                히어로 배경색
                <span className="admin-field-badge">이미지 없거나 투명일 때 표시</span>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.375rem' }}>
                  <input
                    type="color"
                    value={form.heroBgColor}
                    onChange={(e) => setForm((f) => ({ ...f, heroBgColor: e.target.value }))}
                    className="admin-color-input"
                  />
                  <input
                    className="admin-input"
                    style={{ flex: 1 }}
                    value={form.heroBgColor}
                    onChange={(e) => setForm((f) => ({ ...f, heroBgColor: e.target.value }))}
                    placeholder="#0a0a0a"
                  />
                  <button
                    type="button"
                    className="admin-btn-ghost"
                    style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem', whiteSpace: 'nowrap' }}
                    onClick={() => setForm((f) => ({ ...f, heroBgColor: '#0a0a0a' }))}
                  >
                    기본값
                  </button>
                </div>
              </label>

              {/* 한 줄 설명 (다운로드 제외) */}
              {!isDownload(proj.project_type) && (
                <label className="admin-label" style={{ gridColumn: '1 / -1' }}>
                  한 줄 설명
                  <span className="admin-field-badge">히어로 하단 + 메인 캐러셀</span>
                  <textarea
                    className="admin-textarea"
                    rows={2}
                    placeholder="한 두 문장으로 소개하는 요약"
                    value={proj.shortDescription}
                    onChange={(e) => setProj((p) => ({ ...p, shortDescription: e.target.value }))}
                    style={{ resize: 'none' }}
                  />
                </label>
              )}

            </div>
          </section>

          {/* ── 팝업 콘텐츠 (Illustration / Content 전용) ────────────── */}
          {!proj.is_main_only && isGallery(proj.project_type) && (
            <section className="admin-form-section">
              <div className="admin-section-header">
                <div>
                  <h3 className="admin-form-section-title" style={{ margin: 0 }}>팝업 콘텐츠</h3>
                  <p className="admin-field-hint" style={{ marginTop: '0.25rem' }}>
                    카드를 클릭하면 뜨는 갤러리 팝업에 그대로 표시됩니다. (상세 페이지 없음)
                  </p>
                </div>
              </div>

              <div className="admin-form-grid">
                <label className="admin-label" style={{ gridColumn: '1 / -1' }}>
                  요약
                  <span className="admin-field-badge">팝업 상단 · 굵지 않은 첫 문단</span>
                  <textarea
                    className="admin-textarea"
                    rows={2}
                    placeholder="한 두 문장 요약 (비워두면 위 '한 줄 설명'이 대신 표시됩니다)"
                    value={proj.summary}
                    onChange={(e) => setProj((p) => ({ ...p, summary: e.target.value }))}
                    style={{ resize: 'none' }}
                  />
                </label>

                <label className="admin-label" style={{ gridColumn: '1 / -1' }}>
                  상세 설명
                  <span className="admin-field-badge">팝업 하단 · 긴 본문</span>
                  <textarea
                    className="admin-textarea"
                    rows={6}
                    placeholder="프로젝트에 대한 자세한 설명"
                    value={proj.description}
                    onChange={(e) => setProj((p) => ({ ...p, description: e.target.value }))}
                  />
                </label>
              </div>

              <div className="admin-sec-images" style={{ marginTop: '1rem' }}>
                <div className="admin-sec-images-header">
                  <span className="admin-sec-image-label">
                    갤러리 이미지 {proj.galleryImages.filter((img) => img.url.trim()).length > 0
                      ? `(${proj.galleryImages.filter((img) => img.url.trim()).length})` : ''}
                  </span>
                  <span className="admin-field-badge">팝업 안 이미지 슬라이더 — 순서대로 표시</span>
                  <button className="admin-btn-add admin-btn-add--sm" onClick={addGalleryImage}>
                    + 이미지 추가
                  </button>
                </div>

                {proj.galleryImages.map((img, i) => (
                  <div key={i} className="admin-sec-image-row">
                    <AdminImageInput
                      value={img.url}
                      onChange={(url) => updateGalleryImage(i, 'url', url)}
                    />
                    <input
                      className="admin-input admin-sec-alt-input"
                      placeholder="Alt 텍스트 (선택)"
                      value={img.alt}
                      onChange={(e) => updateGalleryImage(i, 'alt', e.target.value)}
                    />
                    <button className="admin-btn-order" onClick={() => moveGalleryImage(i, -1)} disabled={i === 0}>↑</button>
                    <button className="admin-btn-order" onClick={() => moveGalleryImage(i, 1)} disabled={i === proj.galleryImages.length - 1}>↓</button>
                    <button className="admin-btn-remove" onClick={() => removeGalleryImage(i)}>×</button>
                  </div>
                ))}

                {proj.galleryImages.length === 0 && (
                  <p className="admin-empty-hint">이미지 없음 — 추가하려면 위 버튼을 클릭하세요.</p>
                )}
              </div>
            </section>
          )}

          {/* ── 콘텐츠 섹션 (UI/UX · Writing 전용, 다운로드·메인전용 제외) ─── */}
          {!proj.is_main_only && !isDownload(proj.project_type) && !isGallery(proj.project_type) && (
            <section className="admin-form-section">
              <div className="admin-section-header">
                <div>
                  <h3 className="admin-form-section-title" style={{ margin: 0 }}>콘텐츠 섹션</h3>
                  <p className="admin-field-hint" style={{ marginTop: '0.25rem' }}>
                    히어로 아래 본문에 표시됩니다. 섹션 이름이 굵은 레이블로 출력됩니다.
                  </p>
                </div>
                <button className="admin-btn-add" onClick={addSection}>+ 섹션 추가</button>
              </div>

              {form.contentSections.map((sec, i) => (
                <div key={i} className={`admin-sec-card${sec.is_visible ? '' : ' admin-sec-card--hidden'}`}>

                  <div className="admin-sec-card-header">
                    <input
                      className="admin-input admin-sec-name-input"
                      placeholder="섹션 이름 (예: Overview, 작업 과정)"
                      value={sec.title}
                      onChange={(e) => updateSection(i, 'title', e.target.value)}
                    />
                    <div className="admin-sec-card-actions">
                      <button
                        className={`admin-btn-toggle${sec.is_visible ? ' admin-btn-toggle--on' : ''}`}
                        onClick={() => updateSection(i, 'is_visible', !sec.is_visible)}
                      >{sec.is_visible ? '노출' : '숨김'}</button>
                      <button className="admin-btn-order" onClick={() => moveSection(i, -1)} disabled={i === 0}>↑</button>
                      <button className="admin-btn-order" onClick={() => moveSection(i, 1)} disabled={i === form.contentSections.length - 1}>↓</button>
                      <button className="admin-btn-remove" onClick={() => removeSection(i)}>×</button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--c-n600)' }}>선택한 텍스트만</span>
                    <button type="button" className="admin-btn-toggle" style={{ fontWeight: 700 }}
                      onClick={() => applyInlineMarkup(i, '**')}
                    >굵게</button>
                    <button type="button" className="admin-btn-toggle" style={{ fontSize: '1.05em' }}
                      onClick={() => applyInlineMarkup(i, '++')}
                    >크게</button>
                    <span className="admin-field-hint" style={{ margin: 0 }}>본문에서 드래그로 선택 후 클릭</span>
                  </div>

                  <textarea
                    ref={(el) => { sectionTextareaRefs.current[i] = el; }}
                    className="admin-textarea"
                    rows={4}
                    placeholder="섹션 본문 텍스트 (선택)"
                    value={sec.content}
                    onChange={(e) => updateSection(i, 'content', e.target.value)}
                  />

                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--c-n600)' }}>섹션 전체 크기</span>
                      {TEXT_SIZE_OPTIONS.map(({ value, label }) => (
                        <button
                          key={value}
                          type="button"
                          className={`admin-btn-toggle${sec.textSize === value ? ' admin-btn-toggle--on' : ''}`}
                          onClick={() => updateSection(i, 'textSize', value)}
                        >{label}</button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--c-n600)' }}>섹션 전체 굵기</span>
                      {TEXT_WEIGHT_OPTIONS.map(({ value, label }) => (
                        <button
                          key={value}
                          type="button"
                          className={`admin-btn-toggle${sec.textWeight === value ? ' admin-btn-toggle--on' : ''}`}
                          onClick={() => updateSection(i, 'textWeight', value)}
                        >{label}</button>
                      ))}
                    </div>
                  </div>

                  {/* YouTube URL */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                    <label style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--c-n600)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      🎬 YouTube URL
                      <span className="admin-field-badge">섹션 내 동영상 임베드</span>
                    </label>
                    <input
                      className="admin-input"
                      placeholder="https://www.youtube.com/watch?v=... 또는 https://youtu.be/..."
                      value={sec.videoUrl || ''}
                      onChange={(e) => updateSection(i, 'videoUrl', e.target.value)}
                    />
                    {sec.videoUrl?.trim() && getYouTubeId(sec.videoUrl) && (
                      <p style={{ fontSize: '0.75rem', color: 'var(--c-hi)', margin: 0 }}>
                        ✓ 유효한 YouTube URL — 상세 페이지에 영상이 표시됩니다
                      </p>
                    )}
                    {sec.videoUrl?.trim() && !getYouTubeId(sec.videoUrl) && (
                      <p style={{ fontSize: '0.75rem', color: '#ef4444', margin: 0 }}>
                        올바른 YouTube URL을 입력해주세요
                      </p>
                    )}
                  </div>

                  <div className="admin-sec-images">
                    <div className="admin-sec-images-header">
                      <span className="admin-sec-image-label">
                        이미지 {sec.images.length > 0 ? `(${sec.images.length})` : ''}
                      </span>
                      <button className="admin-btn-add admin-btn-add--sm" onClick={() => addSectionImage(i)}>
                        + 이미지 추가
                      </button>
                    </div>

                    {sec.images.map((img, j) => (
                      <div key={j} className="admin-sec-image-row">
                        <AdminImageInput
                          value={img.url}
                          onChange={(url) => updateSectionImage(i, j, 'url', url)}
                        />
                        <input
                          className="admin-input admin-sec-alt-input"
                          placeholder="Alt 텍스트 (선택)"
                          value={img.alt}
                          onChange={(e) => updateSectionImage(i, j, 'alt', e.target.value)}
                        />
                        <button className="admin-btn-remove" onClick={() => removeSectionImage(i, j)}>×</button>
                      </div>
                    ))}

                    {sec.images.length === 0 && (
                      <p className="admin-empty-hint">이미지 없음 — 추가하려면 위 버튼을 클릭하세요.</p>
                    )}
                  </div>
                </div>
              ))}

              {form.contentSections.length === 0 && (
                <p className="admin-empty-hint">섹션이 없습니다. "+ 섹션 추가"를 눌러 추가하세요.</p>
              )}
            </section>
          )}

          <div className="admin-form-footer">
            {error && <p className="admin-error">{error}</p>}
            <button onClick={handleSave} disabled={saving} className="admin-btn-primary">
              {saving ? '저장 중…' : '저장'}
            </button>
          </div>
        </main>
      </div>
    </AdminGuard>
  );
}
